<script>
  import { useRoute, useRouter } from '@dvcol/svelte-simple-router';
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../../stores/authStore.svelte.js';
  import PageHeader from '../../components/PageHeader.svelte';
  import ConfirmModal from '../../components/ConfirmModal.svelte';
  import MembersPanel from '../../components/tenant/MembersPanel.svelte';
  import ReadinessPanel from '../../components/tenant/ReadinessPanel.svelte';
  import IntegrationsPanel from '../../components/tenant/IntegrationsPanel.svelte';
  import JobsPanel from '../../components/tenant/JobsPanel.svelte';
  import ActivityPanel from '../../components/tenant/ActivityPanel.svelte';

  // Route params must come from useRoute(), not $props() — see PropertyDetail.svelte.
  const { route, location } = useRoute();
  const router = useRouter();
  const tenantId = $derived(Number(location?.params?.id ?? route?.params?.id));

  let loading = $state(true);
  let error = $state(null);
  let tenant = $state(null);

  let tab = $state('overview');
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fa-building' },
    { id: 'readiness', label: 'Readiness', icon: 'fa-list-check' },
    { id: 'members', label: 'Users', icon: 'fa-users' },
    { id: 'integrations', label: 'Integrations', icon: 'fa-plug' },
    { id: 'jobs', label: 'Jobs', icon: 'fa-gears' },
    { id: 'activity', label: 'Activity', icon: 'fa-clock-rotate-left' },
  ];

  /** Readiness-checklist actions that move somewhere else. */
  async function navigateFromCheck(actionId) {
    const toTab = {
      open_integrations: 'integrations', open_notifications: 'integrations',
      open_jobs: 'jobs', open_members: 'members',
    }[actionId];
    if (toTab) { tab = toTab; return; }
    if (actionId === 'reactivate') { statusModal = 'reactivate'; return; }
    // Tenant-scoped staff screens: switch the viewing context to this tenant first.
    const route = { open_settings: 'tenant-settings', open_unlinked: 'unlinked' }[actionId];
    if (route) {
      await authStore.loadContext(tenant.id);
      window.dispatchEvent(new CustomEvent('strao:tenant-changed'));
      router.push({ name: route });
    }
  }

  let form = $state({ name: '', email: '', phone: '', address: '' });
  let saving = $state(false);

  let statusModal = $state(null);   // 'deactivate' | 'reactivate' | null
  let statusReason = $state('');
  let changingStatus = $state(false);

  function fillForm(t) {
    form = { name: t.name ?? '', email: t.email ?? '', phone: t.phone ?? '', address: t.address ?? '' };
  }

  async function load() {
    loading = true;
    error = null;
    try {
      tenant = await api.getTenant(tenantId);
      fillForm(tenant);
    } catch (e) {
      error = e.status === 404 ? 'Tenant not found.' : e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { if (tenantId) load(); });

  const dirty = $derived(
    !!tenant && (
      form.name.trim() !== (tenant.name ?? '')
      || form.email.trim() !== (tenant.email ?? '')
      || form.phone.trim() !== (tenant.phone ?? '')
      || form.address.trim() !== (tenant.address ?? '')
    )
  );

  async function save(event) {
    event.preventDefault();
    saving = true;
    try {
      const renamed = form.name.trim() !== tenant.name;
      tenant = await api.updateTenant(tenant.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
      });
      fillForm(tenant);
      toast.success('Tenant details saved');
      if (renamed) await authStore.loadContext(authStore.tenantId);
    } catch (e) {
      toast.error(e.message);
    } finally {
      saving = false;
    }
  }

  async function applyStatus() {
    const action = statusModal;
    changingStatus = true;
    try {
      tenant = action === 'deactivate'
        ? await api.deactivateTenant(tenant.id, statusReason.trim())
        : await api.reactivateTenant(tenant.id, statusReason.trim());
      toast.success(action === 'deactivate' ? `${tenant.name} deactivated` : `${tenant.name} reactivated`);
      statusModal = null;
      statusReason = '';
      await authStore.loadContext(authStore.tenantId);
    } catch (e) {
      toast.error(e.message);
    } finally {
      changingStatus = false;
    }
  }
</script>

{#if loading && !tenant}
  <p class="p-card p-4 text-sm p-muted">Loading…</p>
{:else if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if tenant}
  <PageHeader title={tenant.name} subtitle={`Tenant #${tenant.id}`}>
    {#snippet children()}
      <button type="button" class="p-btn p-btn-quiet" onclick={() => router.push({ name: 'admin-tenants' })}>
        <i class="fas fa-arrow-left" aria-hidden="true"></i> All tenants
      </button>
      {#if tenant.is_active}
        <button type="button" class="p-btn p-btn-danger" onclick={() => (statusModal = 'deactivate')}>
          Deactivate
        </button>
      {:else}
        <button type="button" class="p-btn p-btn-primary" onclick={() => (statusModal = 'reactivate')}>
          Reactivate
        </button>
      {/if}
    {/snippet}
  </PageHeader>

  {#if !tenant.is_active}
    <div class="mb-6 rounded-lg px-4 py-3 text-sm" style="background-color: var(--p-danger-bg); color: var(--p-danger)">
      <p class="font-semibold"><i class="fas fa-ban" aria-hidden="true"></i> This tenant is deactivated.</p>
      <p class="mt-1">
        Scheduled jobs skip it, incoming guest messages are logged but ignored, nothing is sent,
        and its users cannot sign in.
      </p>
      {#if tenant.deactivation}
        <p class="mt-1 text-xs">
          {new Date(tenant.deactivation.at).toLocaleString()}
          {#if tenant.deactivation.by_email}by {tenant.deactivation.by_email}{/if}
          {#if tenant.deactivation.reason}— “{tenant.deactivation.reason}”{/if}
        </p>
      {/if}
    </div>
  {/if}

  <div class="mb-6 flex gap-1 overflow-x-auto" role="tablist" style="border-bottom: 1px solid var(--p-border)">
    {#each tabs as t}
      <button type="button" role="tab" aria-selected={tab === t.id}
              class="whitespace-nowrap px-3 py-2 text-sm"
              style={tab === t.id
                ? 'border-bottom: 2px solid var(--p-brand); color: var(--p-brand)'
                : 'color: var(--p-text-muted)'}
              onclick={() => (tab = t.id)}>
        <i class="fas {t.icon}" aria-hidden="true"></i> {t.label}
      </button>
    {/each}
  </div>

  {#if tab === 'overview'}
    <form class="p-card max-w-2xl space-y-4 p-4" onsubmit={save}>
      <div class="grid gap-4 sm:grid-cols-2">
        <div class="sm:col-span-2">
          <label for="d-name" class="text-xs font-medium p-muted">Name</label>
          <input id="d-name" class="p-input mt-1" bind:value={form.name} required maxlength="255" />
        </div>
        <div>
          <label for="d-email" class="text-xs font-medium p-muted">Contact email</label>
          <input id="d-email" class="p-input mt-1" type="email" bind:value={form.email} maxlength="255" />
        </div>
        <div>
          <label for="d-phone" class="text-xs font-medium p-muted">Phone</label>
          <input id="d-phone" class="p-input mt-1" bind:value={form.phone} maxlength="50" />
        </div>
        <div class="sm:col-span-2">
          <label for="d-address" class="text-xs font-medium p-muted">Address</label>
          <textarea id="d-address" class="p-input mt-1" rows="2" bind:value={form.address}></textarea>
        </div>
      </div>
      <div class="flex items-center justify-between pt-2" style="border-top: 1px solid var(--p-border)">
        <p class="text-xs p-faint">
          Created {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '—'}
          · updated {tenant.updated_at ? new Date(tenant.updated_at).toLocaleString() : '—'}
        </p>
        <button type="submit" class="p-btn p-btn-primary" disabled={saving || !dirty || !form.name.trim()}>
          Save details
        </button>
      </div>
    </form>
  {:else if tab === 'readiness'}
    <ReadinessPanel {tenant} onNavigate={navigateFromCheck} />
  {:else if tab === 'members'}
    <MembersPanel {tenant} />
  {:else if tab === 'integrations'}
    <IntegrationsPanel {tenant} />
  {:else if tab === 'jobs'}
    <JobsPanel {tenant} />
  {:else if tab === 'activity'}
    <ActivityPanel {tenant} />
  {/if}
{/if}

<ConfirmModal open={!!statusModal}
              title={statusModal === 'deactivate' ? `Deactivate ${tenant?.name}?` : `Reactivate ${tenant?.name}?`}
              message={statusModal === 'deactivate'
                ? 'Everything stops for this tenant: scheduled syncs skip it, guest messages are logged but not answered, no replies are sent, and its users lose portal access. Data is kept and this is reversible.'
                : 'Syncs, guest-message processing and user access resume. Messages that arrived while it was deactivated are not drafted retroactively.'}
              confirmLabel={statusModal === 'deactivate' ? 'Deactivate tenant' : 'Reactivate tenant'}
              variant={statusModal === 'deactivate' ? 'danger' : 'primary'}
              confirmDisabled={!statusReason.trim() || changingStatus}
              onConfirm={applyStatus}
              onCancel={() => { statusModal = null; statusReason = ''; }}>
  {#snippet children()}
    <label for="status-reason" class="mt-4 block text-xs font-medium p-muted">Reason (required, recorded in the audit log)</label>
    <input id="status-reason" class="p-input mt-1" bind:value={statusReason} maxlength="500" />
  {/snippet}
</ConfirmModal>
